import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

import { DashboardSettings } from '@/pages/dashboard/types';
import { statsApi } from '@/services/statistics';
import { Building2, Euro, Users, Clock, Receipt, Star, Award } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useHideAmounts } from '@/hooks/useHideAmounts';
import { RevenueTrendsChart } from './RevenueTrendsChart';

interface FirmStatisticsProps {
  settings: DashboardSettings;
}

export function FirmStatistics({ settings }: FirmStatisticsProps) {
  const { user } = useAuth();
  const { hideAmounts } = useHideAmounts();
  const [monthlyStats, setMonthlyStats] = useState<any>(null);
  const [yearlyStats, setYearlyStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const firmId = user?.firms?.[0] || 1;
        
        // Fetch comprehensive data with full details
        const [monthlyData, yearlyData] = await Promise.all([
          statsApi.getComprehensiveFirmStats(firmId, { period: 'month', detail: 'full' }),
          statsApi.getComprehensiveFirmStats(firmId, { period: 'year', detail: 'full' })
        ]);
        
        setMonthlyStats(monthlyData);
        setYearlyStats(yearlyData);
      } catch (err) {
        console.error('Error fetching firm stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchStats();
    }
  }, [user]);

  // Helper functions
  const formatCurrency = (amount: number): string => {
    if (hideAmounts) return '•••';
    return new Intl.NumberFormat('en-BE', {
      style: 'currency',
      currency: settings.currency || 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatHours = (hours: number): string => {
    return `${Math.round(hours)}h`;
  };



  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Firm Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || (!monthlyStats && !yearlyStats)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Firm Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground">Failed to load firm statistics</p>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
        </CardContent>
      </Card>
    );
  }



  // Helper function to render consolidated stats view
  const renderConsolidatedView = () => {
    if (!monthlyStats && !yearlyStats) return null;

    const getMonthlyValue = (path: string) => {
      const keys = path.split('.');
      let value = monthlyStats;
      for (const key of keys) {
        value = value?.[key];
      }
      return value || 0;
    };

    const getYearlyValue = (path: string) => {
      const keys = path.split('.');
      let value = yearlyStats;
      for (const key of keys) {
        value = value?.[key];
      }
      return value || 0;
    };

    return (
      <div className="space-y-6">
        {/* Revenue Performance Row */}
        <div className="border rounded-lg p-6 bg-card">
          <div className="flex items-center gap-2 mb-4">
            <Euro className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Revenue Performance</h3>
          </div>
          <div className="grid grid-cols-3 gap-8">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Revenue Earned</div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatCurrency(getMonthlyValue('financial_metrics.total_revenue_actual'))}
              </div>
              <div className="text-xs text-muted-foreground">This Month</div>
              <div className="text-lg font-medium tabular-nums mt-1">
                {formatCurrency(getYearlyValue('financial_metrics.total_revenue_actual'))}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Outstanding WIP</div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatCurrency(getYearlyValue('financial_metrics.outstanding_revenue_actual'))}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
              <div className="text-lg font-medium tabular-nums mt-1">
                {formatHours(getYearlyValue('financial_metrics.wip_hours') || getMonthlyValue('financial_metrics.wip_hours'))}
              </div>
              <div className="text-xs text-muted-foreground">Unbilled Hours</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Average Rate</div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatCurrency(getMonthlyValue('financial_metrics.average_rate'))}/h
              </div>
              <div className="text-xs text-muted-foreground">This Month</div>
              <div className="text-lg font-medium tabular-nums mt-1">
                {formatCurrency(getYearlyValue('financial_metrics.average_rate'))}/h
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
            </div>
          </div>
        </div>

        {/* Billing & Collections Row */}
        <div className="border rounded-lg p-6 bg-card">
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Billing & Collections</h3>
          </div>
          <div className="grid grid-cols-3 gap-8">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Total Invoiced</div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatCurrency(getYearlyValue('billing_metrics.total_invoiced'))}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
              <div className="text-lg font-medium tabular-nums mt-1">
                {getYearlyValue('billing_metrics.invoice_count')} invoices
              </div>
              <div className="text-xs text-muted-foreground">Total Count</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Collections (YTD)</div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatCurrency(getYearlyValue('billing_metrics.total_paid'))}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
              <div className="text-lg font-medium tabular-nums mt-1">
                {(getYearlyValue('billing_metrics.collection_rate') || 0).toFixed(1)}%
              </div>
              <div className="text-xs text-muted-foreground">Collection Rate</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Outstanding</div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatCurrency(getYearlyValue('billing_metrics.total_outstanding'))}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
              <div className="text-lg font-medium tabular-nums mt-1">
                {getYearlyValue('billing_metrics.pending_invoices')} pending
              </div>
              <div className="text-xs text-muted-foreground">Invoices</div>
            </div>
          </div>
        </div>

        {/* Time & Utilization Row */}
        <div className="border rounded-lg p-6 bg-card">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Time & Utilization</h3>
          </div>
          <div className="grid grid-cols-3 gap-8">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Billable Hours</div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatHours(getMonthlyValue('time_metrics.billable_hours_actual'))}
              </div>
              <div className="text-xs text-muted-foreground">This Month</div>
              <div className="text-lg font-medium tabular-nums mt-1">
                {formatHours(getYearlyValue('time_metrics.billable_hours_actual'))}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Billable vs Non-Billable</div>
              <div className="text-2xl font-semibold tabular-nums">
                {(getMonthlyValue('time_metrics.utilization_rate')).toFixed(1)}%
              </div>
              <div className="text-xs text-muted-foreground">This Month</div>
              <div className="text-lg font-medium tabular-nums mt-1">
                {(getYearlyValue('time_metrics.utilization_rate')).toFixed(1)}%
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Team Size</div>
              <div className="text-2xl font-semibold tabular-nums">
                {getMonthlyValue('user_metrics.active_users') || getMonthlyValue('summary.active_users')}
              </div>
              <div className="text-xs text-muted-foreground">Active Lawyers</div>
            </div>
          </div>
        </div>

        {/* Client & Matter Portfolio Row */}
        <div className="border rounded-lg p-6 bg-card">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Client Portfolio</h3>
          </div>
          <div className="grid grid-cols-4 gap-6">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Active Clients</div>
              <div className="text-2xl font-semibold tabular-nums">
                {getYearlyValue('client_metrics.active_clients') || getYearlyValue('summary.active_clients')}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Active Matters</div>
              <div className="text-2xl font-semibold tabular-nums">
                {getYearlyValue('matter_metrics.active_matters') || getYearlyValue('summary.active_matters')}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Revenue/Client</div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatCurrency(
                  getYearlyValue('client_metrics.avg_revenue_per_client') ||
                  ((getYearlyValue('financial_metrics.total_revenue_actual') && getYearlyValue('client_metrics.active_clients')) ?
                    getYearlyValue('financial_metrics.total_revenue_actual') / getYearlyValue('client_metrics.active_clients') : 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Revenue/Matter</div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatCurrency(
                  getYearlyValue('matter_metrics.avg_revenue_per_matter') ||
                  ((getYearlyValue('financial_metrics.total_revenue_actual') && getYearlyValue('matter_metrics.active_matters')) ?
                    getYearlyValue('financial_metrics.total_revenue_actual') / getYearlyValue('matter_metrics.active_matters') : 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground">Year to Date</div>
            </div>
          </div>
        </div>

                {/* Top Performers Section */}
        {(yearlyStats?.top_performers?.clients?.length > 0 || yearlyStats?.top_performers?.users?.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Clients */}
            {yearlyStats?.top_performers?.clients?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Star className="h-5 w-5 text-muted-foreground" />
                    Top Clients (YTD)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {yearlyStats.top_performers.clients.slice(0, 5).map((client: any, index: number) => (
                      <div key={client.matter__clients__id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center p-0">
                            {index + 1}
                          </Badge>
                          <div>
                            <div className="font-medium text-sm">{client.matter__clients__name}</div>
                            <div className="text-xs text-muted-foreground">{client.entries} entries</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">{formatCurrency(client.revenue)}</div>
                          <div className="text-xs text-muted-foreground">{formatHours(client.hours)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Users */}
            {yearlyStats?.top_performers?.users?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Award className="h-5 w-5 text-muted-foreground" />
                    Top Performers (YTD)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {yearlyStats.top_performers.users.slice(0, 5).map((user: any, index: number) => (
                      <div key={user.user__id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center p-0">
                            {index + 1}
                          </Badge>
                          <div>
                            <div className="font-medium text-sm">{user.user__first_name} {user.user__last_name}</div>
                            <div className="text-xs text-muted-foreground">{formatCurrency(user.avg_rate)}/hour</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">{formatCurrency(user.revenue)}</div>
                          <div className="text-xs text-muted-foreground">{formatHours(user.hours)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Revenue & billing trends */}
        <RevenueTrendsChart firmId={user?.firms?.[0] || 1} hideAmounts={hideAmounts} />

        {/* Progress indicator for current month */}
        {monthlyStats?.progress_percentage && (
          <div className="p-4 rounded-lg bg-muted/10 border">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium">Current Month Progress</span>
              <span className="font-semibold">{monthlyStats.progress_percentage}% complete</span>
            </div>
            <div className="w-full bg-muted rounded-full h-3">
              <div 
                className="bg-primary h-3 rounded-full transition-all duration-300" 
                style={{ width: `${monthlyStats.progress_percentage}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Building2 className="h-5 w-5" />
          Firm Overview
          {monthlyStats?.progress_percentage && (
            <Badge variant="secondary" className="ml-2 text-xs">
              {monthlyStats.progress_percentage}% month complete
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {renderConsolidatedView()}
      </CardContent>
    </Card>
  );
}